/**
 * @fileoverview Domain card component showing vendors in a tech domain
 * @module TechStackAnalysis/components/DomainCard
 */

import React from 'react';
import {
  Network, Shield, Cloud, Database, Server,
  Users, Headphones, AppWindow, Monitor
} from 'lucide-react';
import VendorPill from './VendorPill';

// Map icon names to components
const ICON_MAP = {
  Network,
  Shield,
  Cloud,
  Database,
  Server,
  Users,
  Headphones,
  AppWindow,
  Monitor
};

/**
 * Format currency value compactly
 * @param {number} value - Value to format
 * @returns {string} Formatted currency string
 */
const formatSpend = (value) => {
  if (!value) return '$0';
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

/**
 * Domain card component
 * @param {Object} props
 * @param {Object} props.domain - Domain data
 * @param {Object} props.selectedVendor - Currently selected vendor
 * @param {Function} props.onVendorClick - Vendor click handler
 * @param {Function} props.onHaptic - Haptic feedback trigger
 */
const DomainCard = ({ domain, selectedVendor, onVendorClick, onHaptic }) => {
  const Icon = ICON_MAP[domain.icon] || AppWindow;

  // Count expiring contracts
  const expiringCount = domain.vendors?.filter(v => {
    if (!v.contractEnd) return false;
    const endDate = new Date(v.contractEnd);
    const in90Days = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);
    return endDate <= in90Days;
  }).length || 0;

  return (
    <div className="ts-domain-card">
      <div className="ts-domain-header">
        <div className="ts-domain-title">
          <div
            className="ts-domain-icon"
            style={{ background: `${domain.color}15`, color: domain.color }}
          >
            <Icon size={18} />
          </div>
          <div>
            <div className="ts-domain-name">{domain.name}</div>
            <div className="ts-domain-count">
              {domain.productCount || domain.vendors?.length || 0} products
            </div>
          </div>
        </div>
        <div className="ts-domain-meta">
          <div className="ts-domain-spend">{formatSpend(domain.totalSpend)}</div>
          {expiringCount > 0 && (
            <div className="ts-domain-expiring">{expiringCount} expiring</div>
          )}
        </div>
      </div>

      <div className="ts-domain-body">
        {domain.vendors && domain.vendors.length > 0 ? (
          <div className="ts-vendor-list">
            {domain.vendors.map((vendor, index) => (
              <VendorPill
                key={vendor.vendorId || index}
                vendor={vendor}
                selected={selectedVendor?.vendorId === vendor.vendorId}
                onClick={onVendorClick}
                onHaptic={onHaptic}
              />
            ))}
          </div>
        ) : (
          <div className="ts-empty-domain">
            No vendors in this domain
          </div>
        )}
      </div>
    </div>
  );
};

export default DomainCard;
